import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { FamilyCoBuildingPlanBadge, userHasFamilyCoBuildingIcon } from '../components/FamilyCoBuildingPlanBadge';
import { QrCodeView } from '../components/QrCodeView';
import { useAuth } from '../context/AuthContext';
import {
  familyPlanService,
  type FamilyPlanAdvantage,
  type FamilyPlanCatalog,
  type FamilyPlanOrder,
  type FamilyPlanPlanner,
  type FamilyPlanSubject,
} from '../services/familyPlanService';

type SubjectPrice = {
  priceYuan: string;
  amountFen: number;
  label: string;
};

const DEFAULT_CATALOG: Pick<
  FamilyPlanCatalog,
  'productName' | 'priceUnit' | 'fluctuationNote' | 'manifesto' | 'eraQuestion' | 'answerLead' | 'advantagesFootnote'
> = {
  productName: '家庭共建计划',
  priceUnit: '单科每月',
  fluctuationNote: '当前按固定月费计价（单科 ¥85.9/月）；每日波动表仍维护，暂不作为计费依据',
  manifesto: 'AI时代，孩子的学习不能再孤军奋战——每个孩子都需要一支自己的学习专家团。',
  eraQuestion:
    '在AI时代，一个普通家庭如何给孩子一支由真人计划师与智能体共同组成的学习专家团，带领完成学科学习过程的闭环落地？',
  answerLead:
    '家庭共建计划，是在回答这个时代问题：真人和智能体专家团队一起，把学科学情诊断、计划落地与复习闭环真正做完。颗粒日记不是替孩子学习，而是让专家团进入孩子学习的全过程。',
  advantagesFootnote: '每个学科服务落地，都可以选择真人计划师专家团补充服务且长期在线。',
};

function formatYuanFromFen(fen: number): string {
  const yuan = (Number(fen) || 0) / 100;
  return yuan.toFixed(1).replace(/\.0$/, '');
}

function orderStatusLabel(status: string): string {
  if (status === 'paid') return '已开通';
  if (status === 'paying') return '支付中';
  if (status === 'pending_payment') return '待支付';
  return status;
}

function canResumePay(status: string): boolean {
  return status === 'pending_payment' || status === 'paying';
}

function NoticeDialog({
  title,
  message,
  tone,
  onClose,
}: {
  title: string;
  message: string;
  tone: 'error' | 'success';
  onClose: () => void;
}) {
  if (typeof document === 'undefined') return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[130] flex items-center justify-center bg-black/45 p-4"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="family-plan-notice-title"
        className="w-full max-w-sm rounded-2xl bg-surface px-5 py-6 shadow-[0_24px_80px_rgba(0,0,0,0.22)]"
      >
        <h3
          id="family-plan-notice-title"
          className={`text-base font-semibold ${tone === 'error' ? 'text-danger' : 'text-primary'}`}
        >
          {title}
        </h3>
        <p className="mt-3 text-sm leading-6 text-ink">{message}</p>
        <button
          type="button"
          onClick={onClose}
          className="mt-5 w-full rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white"
        >
          知道了
        </button>
      </div>
    </div>,
    document.body,
  );
}

export function FamilyPlanPage() {
  const { user, refreshProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [productName, setProductName] = useState(DEFAULT_CATALOG.productName);
  const [manifesto, setManifesto] = useState(DEFAULT_CATALOG.manifesto);
  const [eraQuestion, setEraQuestion] = useState(DEFAULT_CATALOG.eraQuestion);
  const [answerLead, setAnswerLead] = useState(DEFAULT_CATALOG.answerLead);
  const [advantages, setAdvantages] = useState<FamilyPlanAdvantage[]>([]);
  const [advantagesFootnote, setAdvantagesFootnote] = useState(DEFAULT_CATALOG.advantagesFootnote);
  const [subjects, setSubjects] = useState<FamilyPlanSubject[]>([]);
  const [planners, setPlanners] = useState<FamilyPlanPlanner[]>([]);
  const [recentOrders, setRecentOrders] = useState<FamilyPlanOrder[]>([]);

  const [selectedSubjects, setSelectedSubjects] = useState<string[]>(['yuwen', 'math', 'english']);
  const [selectedPlannerId, setSelectedPlannerId] = useState('');
  const [priceMap, setPriceMap] = useState<Record<string, SubjectPrice>>({});
  const [pricingDate, setPricingDate] = useState('');
  const [priceUnit, setPriceUnit] = useState(DEFAULT_CATALOG.priceUnit);
  const [fluctuationNote, setFluctuationNote] = useState(DEFAULT_CATALOG.fluctuationNote);

  const [payOrderId, setPayOrderId] = useState('');
  const [payCodeUrl, setPayCodeUrl] = useState('');
  const pollTimerRef = useRef<number | null>(null);

  const hasPlan = userHasFamilyCoBuildingIcon(user);

  const billing = useMemo(() => {
    let totalFen = 0;
    const parts: string[] = [];
    selectedSubjects.forEach((code) => {
      const row = priceMap[code];
      if (!row) return;
      totalFen += Number(row.amountFen) || 0;
      parts.push(`${row.label || code} ¥${row.priceYuan}`);
    });
    return {
      selectedSubjectCount: selectedSubjects.length,
      totalPriceYuan: totalFen ? formatYuanFromFen(totalFen) : '0',
      priceUnitLabel: selectedSubjects.length > 1 ? `${selectedSubjects.length}科每月` : '单科每月',
      billingSummary: selectedSubjects.length
        ? `${selectedSubjects.length} 科合计 ¥${formatYuanFromFen(totalFen)}/月（${parts.join('、')}）`
        : '请选择至少一个学科',
    };
  }, [priceMap, selectedSubjects]);

  const selectedPlanner = planners.find((item) => item.userId === selectedPlannerId) || null;

  const decoratedSubjects = useMemo(
    () =>
      subjects.map((item) => ({
        ...item,
        active: selectedSubjects.includes(item.code),
        priceYuan: priceMap[item.code]?.priceYuan || item.priceYuan || '',
      })),
    [priceMap, selectedSubjects, subjects],
  );

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current != null) {
      window.clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const closePayModal = useCallback(() => {
    stopPolling();
    setPayOrderId('');
    setPayCodeUrl('');
    setSubmitting(false);
  }, [stopPolling]);

  const reload = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [catalog, pricing, nextPlanners, orders] = await Promise.all([
        familyPlanService.loadCatalog(),
        familyPlanService.loadPricing(),
        familyPlanService.listPlanners(),
        familyPlanService.listMyOrders().catch(() => [] as FamilyPlanOrder[]),
      ]);
      const nextPriceMap: Record<string, SubjectPrice> = {};
      (pricing.subjects || []).forEach((item) => {
        if (!item?.code) return;
        nextPriceMap[item.code] = {
          priceYuan: item.priceYuan,
          amountFen: Number(item.amountFen) || 0,
          label: item.label,
        };
      });
      setProductName(catalog.productName || DEFAULT_CATALOG.productName);
      setManifesto(catalog.manifesto || DEFAULT_CATALOG.manifesto);
      setEraQuestion(catalog.eraQuestion || DEFAULT_CATALOG.eraQuestion);
      setAnswerLead(catalog.answerLead || DEFAULT_CATALOG.answerLead);
      setAdvantages(catalog.advantages || []);
      setAdvantagesFootnote(catalog.advantagesFootnote || DEFAULT_CATALOG.advantagesFootnote);
      setSubjects(catalog.subjects || []);
      setPlanners(nextPlanners);
      setRecentOrders(orders.slice(0, 5));
      setPriceMap(nextPriceMap);
      setPricingDate(pricing.pricingDate || '');
      setPriceUnit(pricing.priceUnit || catalog.priceUnit || DEFAULT_CATALOG.priceUnit);
      setFluctuationNote(pricing.fluctuationNote || catalog.fluctuationNote || DEFAULT_CATALOG.fluctuationNote);
      setSelectedPlannerId((current) => current || nextPlanners[0]?.userId || '');
      setSelectedSubjects((current) => {
        const offered = (pricing.subjects || []).map((item) => item.code).filter(Boolean);
        const defaults = ['yuwen', 'math', 'english'].filter((code) => offered.includes(code));
        const fallback = defaults.length ? defaults : offered;
        const next = (current.length ? current : fallback).filter((code) => offered.includes(code));
        return next.length ? next : fallback;
      });
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
    return stopPolling;
  }, [reload, stopPolling]);

  const startPayPolling = useCallback(
    (orderId: string) => {
      stopPolling();
      pollTimerRef.current = window.setInterval(() => {
        void (async () => {
          try {
            const status = await familyPlanService.getPayStatus(orderId);
            if (status.paymentStatus === 'paid' || status.alreadyPaid) {
              stopPolling();
              setPayOrderId('');
              setPayCodeUrl('');
              setSubmitting(false);
              setSuccess('家庭计划已开通');
              setError('');
              await refreshProfile().catch(() => undefined);
              await reload();
            }
          } catch {
            // keep polling until modal closed
          }
        })();
      }, 1500);
    },
    [refreshProfile, reload, stopPolling],
  );

  const beginNativePay = useCallback(
    async (orderId: string): Promise<'paid' | 'qr'> => {
      const pay = await familyPlanService.createNativePay(orderId);
      if (pay.alreadyPaid || pay.paymentStatus === 'paid') {
        setSuccess('家庭计划已开通');
        await refreshProfile().catch(() => undefined);
        await reload();
        return 'paid';
      }
      const codeUrl = String(pay.codeUrl || '').trim();
      if (!codeUrl) {
        throw new Error('未能生成微信支付二维码，请稍后重试');
      }
      setPayOrderId(orderId);
      setPayCodeUrl(codeUrl);
      startPayPolling(orderId);
      return 'qr';
    },
    [refreshProfile, reload, startPayPolling],
  );

  const onToggleSubject = useCallback((code: string) => {
    setSelectedSubjects((current) => {
      const index = current.indexOf(code);
      if (index >= 0) {
        if (current.length <= 1) {
          setError('请至少保留一个学科');
          return current;
        }
        setError('');
        return current.filter((item) => item !== code);
      }
      setError('');
      return [...current, code];
    });
  }, []);

  const onSubmit = useCallback(async () => {
    if (submitting) return;
    if (!selectedPlannerId) {
      setError('请先选择一位计划师');
      return;
    }
    if (!selectedSubjects.length) {
      setError('请至少选择一个学科');
      return;
    }
    if (!user?.id) {
      setError('用户信息缺失，请重新登录');
      return;
    }
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      const order = await familyPlanService.createOrder({
        plannerUserId: selectedPlannerId,
        subjects: selectedSubjects,
      });
      const result = await beginNativePay(order.id);
      if (result !== 'qr') setSubmitting(false);
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : '开通失败');
      closePayModal();
      setSubmitting(false);
    }
  }, [beginNativePay, closePayModal, selectedPlannerId, selectedSubjects, submitting, user?.id]);

  const onResumePay = useCallback(
    async (orderId: string) => {
      if (submitting) return;
      setSubmitting(true);
      setError('');
      setSuccess('');
      try {
        const result = await beginNativePay(orderId);
        if (result !== 'qr') setSubmitting(false);
      } catch (payError) {
        setError(payError instanceof Error ? payError.message : '发起支付失败');
        closePayModal();
        setSubmitting(false);
      }
    },
    [beginNativePay, closePayModal, submitting],
  );

  const payDisabled = submitting || !selectedPlannerId || !selectedSubjects.length || loading;

  return (
    <div className="relative mx-auto max-w-2xl pb-28">
      <div className="overflow-hidden rounded-2xl border border-border">
      <header className="bg-primary/10 px-5 py-6">
        <div className="flex items-center gap-2 text-xs font-semibold tracking-wide text-muted">
          <FamilyCoBuildingPlanBadge size={14} />
          颗粒日记 · {productName}
          {hasPlan ? (
            <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[11px] font-medium text-primary">已开通</span>
          ) : null}
        </div>
        <h1 className="mt-3 text-xl font-extrabold leading-snug text-primary">{manifesto}</h1>
        <p className="mt-3 text-sm font-semibold leading-relaxed text-ink">{eraQuestion}</p>
      </header>
      <img
        src="/kelilearning-flo.png"
        alt="家庭共建计划：真人计划师与智能体专家团"
        className="block w-full bg-[#0b1220]"
      />

      <section className="bg-surface px-5 py-5">
        <p className="text-sm font-semibold leading-relaxed text-ink">{answerLead}</p>
      </section>
      </div>

      {error ? (
        <NoticeDialog title="提示" message={error} tone="error" onClose={() => setError('')} />
      ) : null}
      {success ? (
        <NoticeDialog title="开通成功" message={success} tone="success" onClose={() => setSuccess('')} />
      ) : null}

      {loading ? (
        <p className="mt-6 text-sm text-muted">加载中…</p>
      ) : (
        <>
          <section className="mt-4 rounded-2xl border border-border bg-surface px-5 py-5">
            <h2 className="text-base font-semibold text-ink">计划落地，家庭能得到什么</h2>
            <ul className="mt-4 space-y-3">
              {advantages.map((item, index) => (
                <li key={item.key} className="flex gap-3">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/15 text-xs font-bold text-primary">
                    {index + 1}
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-ink">{item.title}</p>
                    <p className="mt-0.5 text-sm text-muted">{item.body}</p>
                  </div>
                </li>
              ))}
            </ul>
            <p className="mt-4 text-xs leading-5 text-muted">{advantagesFootnote}</p>
          </section>

          <section className="mt-4 rounded-2xl border border-border bg-surface px-5 py-5">
            <div className="rounded-2xl bg-canvas px-4 py-5 text-center">
              <p className="text-xs font-semibold text-primary">固定月费</p>
              <p className="mt-1 text-3xl font-extrabold text-primary">
                ¥{billing.totalPriceYuan}
                <span className="ml-1 text-sm font-semibold text-muted">/{billing.priceUnitLabel}</span>
              </p>
              <p className="mt-2 text-xs leading-5 text-muted">{fluctuationNote}</p>
              {pricingDate ? <p className="mt-1 text-[11px] text-muted">定价日 {pricingDate}</p> : null}
            </div>
            {billing.selectedSubjectCount > 0 ? (
              <p className="mt-3 text-xs font-medium text-ink">{billing.billingSummary}</p>
            ) : null}
          </section>

          <section className="mt-4 rounded-2xl border border-border bg-surface px-5 py-5">
            <h2 className="text-base font-semibold text-ink">选择学科</h2>
            <p className="mt-1 text-xs text-muted">暂时开放语文、数学、英语；每科按固定月费计费，账单随所选学科累加。</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {decoratedSubjects.map((item) => (
                <button
                  key={item.code}
                  type="button"
                  onClick={() => onToggleSubject(item.code)}
                  className={`rounded-full border px-3 py-1.5 text-sm transition ${
                    item.active
                      ? 'border-primary bg-primary text-white'
                      : 'border-border bg-canvas text-ink hover:border-primary/40'
                  }`}
                >
                  {item.label}
                  {item.priceYuan ? <span className="ml-1 opacity-80">¥{item.priceYuan}</span> : null}
                </button>
              ))}
            </div>
          </section>

          <section className="mt-4 rounded-2xl border border-border bg-surface px-5 py-5">
            <h2 className="text-base font-semibold text-ink">选择计划师</h2>
            <p className="mt-1 text-xs text-muted">提交后，所选计划师将与当前颗粒日记账户绑定，发起家庭共建。</p>
            {planners.length === 0 ? (
              <p className="mt-3 text-sm text-muted">暂无可选计划师，请稍后再试或联系运营开通计划师。</p>
            ) : (
              <div className="mt-3 space-y-2">
                {planners.map((item) => {
                  const active = item.userId === selectedPlannerId;
                  return (
                    <button
                      key={item.userId}
                      type="button"
                      onClick={() => setSelectedPlannerId(item.userId)}
                      className={`flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left transition ${
                        active ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'
                      }`}
                    >
                      <span>
                        <span className="block text-sm font-semibold text-ink">{item.label}</span>
                        <span className="mt-0.5 block text-xs text-muted">{item.phoneMasked || '计划师'}</span>
                      </span>
                      {active ? <span className="text-xs font-semibold text-primary">已选</span> : null}
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          {recentOrders.length ? (
            <section className="mt-4 rounded-2xl border border-border bg-surface px-5 py-5">
              <h2 className="text-base font-semibold text-ink">我的家庭计划</h2>
              <ul className="mt-3 divide-y divide-border">
                {recentOrders.map((order) => (
                  <li key={order.id} className="flex items-center justify-between gap-3 py-3">
                    <div>
                      <p className="text-sm font-medium text-ink">
                        {order.subjectLabel} · {order.plannerLabel}
                      </p>
                      <p className="mt-0.5 text-xs text-muted">
                        ¥{formatYuanFromFen(order.amountFen)} · {orderStatusLabel(order.status)}
                      </p>
                    </div>
                    {canResumePay(order.status) ? (
                      <button
                        type="button"
                        disabled={submitting}
                        onClick={() => void onResumePay(order.id)}
                        className="shrink-0 rounded-lg border border-primary/30 px-3 py-1.5 text-xs font-medium text-primary disabled:opacity-50"
                      >
                        继续支付
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </>
      )}

      <div className="sticky bottom-0 z-10 -mx-4 mt-6 border-t border-border bg-surface/95 px-4 py-3 backdrop-blur sm:mx-0 sm:rounded-2xl sm:border">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-lg font-extrabold text-primary">
              ¥{billing.totalPriceYuan}
              <span className="ml-1 text-xs font-semibold text-muted">/{billing.priceUnitLabel}</span>
            </p>
            <p className="text-xs text-muted">
              {selectedPlanner?.label || '请选择计划师'} · {billing.selectedSubjectCount} 科 · 固定月费
            </p>
          </div>
          <button
            type="button"
            disabled={payDisabled}
            onClick={() => void onSubmit()}
            className="rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? '处理中…' : '绑定并支付开通'}
          </button>
        </div>
      </div>

      {payOrderId && payCodeUrl ? (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-surface px-5 py-6 text-center shadow-[0_24px_80px_rgba(0,0,0,0.22)]">
            <h3 className="text-base font-semibold text-ink">微信扫码支付</h3>
            <p className="mt-1 text-xs text-muted">请使用微信扫描二维码完成家庭计划开通</p>
            <div className="mx-auto mt-4 flex h-52 w-52 items-center justify-center rounded-xl border border-border bg-white p-2">
              <QrCodeView value={payCodeUrl} size={192} label="家庭计划微信支付二维码" />
            </div>
            <p className="mt-3 text-lg font-extrabold text-primary">¥{billing.totalPriceYuan}</p>
            <p className="mt-1 text-xs text-muted">支付完成后本页会自动开通；也可稍后在「我的家庭计划」继续。</p>
            <button
              type="button"
              onClick={closePayModal}
              className="mt-4 rounded-lg px-4 py-2 text-sm text-muted hover:bg-canvas"
            >
              关闭
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
